/**
 * FeatureTag Component
 *
 * Animated feature capability tags with micro-interactions.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Calendar, Mail, CheckSquare, FileText, Mic, Camera, Globe } from 'lucide-react';
import './FeatureTag.css';

// Feature icon mapping
const FEATURE_ICONS = {
  Vision: Eye,
  Calendar: Calendar,
  Gmail: Mail,
  Tasks: CheckSquare,
  Notes: FileText,
  Voice: Mic,
  Camera: Camera,
  Web: Globe
};

// Feature color mapping
const FEATURE_COLORS = {
  Vision: { main: '#9b59b6', light: 'rgba(155, 89, 182, 0.15)' },
  Calendar: { main: '#3498db', light: 'rgba(52, 152, 219, 0.15)' },
  Gmail: { main: '#e74c3c', light: 'rgba(231, 76, 60, 0.15)' },
  Tasks: { main: '#2ecc71', light: 'rgba(46, 204, 113, 0.15)' },
  Notes: { main: '#f39c12', light: 'rgba(243, 156, 18, 0.15)' },
  Voice: { main: '#1abc9c', light: 'rgba(26, 188, 156, 0.15)' },
  Camera: { main: '#e91e63', light: 'rgba(233, 30, 99, 0.15)' },
  Web: { main: '#00bcd4', light: 'rgba(0, 188, 212, 0.15)' }
};

const FeatureTag = ({
  feature,
  isActive = false,
  onClick,
  delay = 0
}) => {
  const Icon = FEATURE_ICONS[feature] || FileText;
  const colors = FEATURE_COLORS[feature] || FEATURE_COLORS.Notes;

  return (
    <motion.div
      className={`feature-tag ${isActive ? 'active' : ''}`}
      initial={{ opacity: 0, y: 10, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.9 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 20,
        delay
      }}
      whileHover={{
        scale: 1.08,
        y: -3,
        boxShadow: `0 6px 20px ${colors.main}40`
      }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      style={{
        '--feature-color': colors.main,
        '--feature-color-light': colors.light,
        borderColor: colors.main,
        background: colors.light
      }}
    >
      {/* Icon with hover animation */}
      <motion.div
        className="feature-icon"
        whileHover={{
          rotate: [0, -15, 15, -10, 10, 0],
          transition: { duration: 0.5 }
        }}
      >
        <Icon size={14} color={colors.main} />
      </motion.div>

      {/* Label */}
      <span className="feature-label" style={{ color: colors.main }}>
        {feature}
      </span>

      {/* Active indicator dot */}
      <AnimatePresence>
        {isActive && (
          <motion.span
            className="feature-active-dot"
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: [1, 1.3, 1],
              opacity: 1
            }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{
              scale: {
                repeat: Infinity,
                duration: 1.5,
                ease: 'easeInOut'
              }
            }}
            style={{ backgroundColor: colors.main }}
          />
        )}
      </AnimatePresence>

      {/* Ripple effect on click */}
      <span className="feature-ripple" />
    </motion.div>
  );
};

// Container component for multiple feature tags
export const FeatureTagsContainer = ({
  features = ['Vision', 'Calendar', 'Gmail', 'Tasks', 'Notes'],
  activeFeatures = [],
  onFeatureClick
}) => {
  return (
    <motion.div
      className="feature-tags-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {features.map((feature, index) => (
        <FeatureTag
          key={feature}
          feature={feature}
          isActive={activeFeatures.includes(feature)}
          onClick={() => onFeatureClick?.(feature)}
          delay={index * 0.08}
        />
      ))}
    </motion.div>
  );
};

export default FeatureTag;
